import { useState } from "react";
import icon from "../../assets/icon.png";
import "./Launch.css";
import Header from "../Header";
import Footer from "../Footer";
import PageTransition from "../../PageTransition";
import { Helmet } from "react-helmet-async";

export default function ContentLaunch() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
  });

  const [status, setStatus] = useState("idle"); // 'idle' | 'submitting' | 'success' | 'error'
  const [errors, setErrors] = useState({});
  const [statusMessage, setStatusMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) {
      errs.name = "Please enter your name";
    }
    if (!formData.email.trim()) {
      errs.email = "Please enter your email";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = "Please enter a valid email address";
    }
    if (!formData.company.trim()) {
      errs.company = "Please enter your company name";
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setStatus("submitting");
    setStatusMessage("");

    const endpoint =
      process.env.REACT_APP_CONTACT_ENDPOINT ||
      "https://000form.com/f/f_kqfpyn2p";

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          Name: formData.name.trim(),
          "Email ID": formData.email.trim(),
          "Company Name": formData.company.trim(),
          _subject: `New Inquiry from ${formData.name.trim()} (${formData.company.trim()})`,
          _captcha: "false",
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && (data.success || response.status === 200)) {
        setStatus("success");
        setStatusMessage(
          data.message || "Thank you! Your information has been received."
        );
        setFormData({ name: "", email: "", company: "" });
      } else if (
        (data.error && data.error.toLowerCase().includes("not verified")) ||
        (data.message && data.message.toLowerCase().includes("not verified"))
      ) {
        setStatus("error");
        setStatusMessage(
          "Activation Required: Please verify your email on your 000form dashboard / Gmail to activate this endpoint."
        );
      } else {
        setStatus("error");
        setStatusMessage(
          data.error ||
          data.message ||
          "Unable to submit at the moment. Please reach out directly to 2lightyear.work@gmail.com"
        );
      }
    } catch (err) {
      console.error("Submission failed:", err);
      setStatus("error");
      setStatusMessage(
        "Network error. Please try again or reach out to 2lightyear.work@gmail.com"
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact 2 Light Years | Start Your Project Today</title>
        <meta
          name="description"
          content="Get in touch with 2 Light Years to start your next high-performance website project. Let's discuss UX, speed, and conversion optimization."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.2lightyears.com/contact" />
        <meta property="og:title" content="Contact 2 Light Years" />
        <meta
          property="og:description"
          content="Start your project with our UX and web performance experts."
        />
        <meta property="og:url" content="https://www.2lightyears.com/contact" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact 2 Light Years" />
        <meta
          name="twitter:description"
          content="Let's build a high-performing website together."
        />
      </Helmet>

      <Header visible mode="normal" />

      <PageTransition>
        <div className="launch-page-wrapper">
          <main className="launch-container">
            <div className="launch-row">
              {/* LEFT: TITLE */}
              <div className="launch-left">
                <h1 className="launch-title">
                  <div className="launch-line launch-line-first">
                    <span>LET&apos;S</span>
                    <img src={icon} alt="" className="launch-icon" />
                  </div>
                  <div className="launch-line">GET IN</div>
                  <span className="launch-highlight">TOUCH</span>
                </h1>
              </div>

              {/* RIGHT: CONTACT FORM & DETAILS (GLASS BOX) */}
              <div className="launch-right launch-glass-box">
                {/* FORM */}
                <form className="launch-form" onSubmit={handleSubmit} noValidate>
                  {/* NAME */}
                  <div className="launch-form-group full-width">
                    <label htmlFor="launch-name" className="launch-label">
                      NAME
                    </label>
                    <input
                      id="launch-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      autoComplete="name"
                      required
                      className={`launch-input ${errors.name ? "has-error" : ""}`}
                    />
                    {errors.name && (
                      <span className="launch-field-error">{errors.name}</span>
                    )}
                  </div>

                  {/* ROW: EMAIL ID & COMPANY NAME */}
                  <div className="launch-form-row">
                    <div className="launch-form-group half-width">
                      <label htmlFor="launch-email" className="launch-label">
                        EMAIL ID
                      </label>
                      <input
                        id="launch-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        autoComplete="email"
                        required
                        className={`launch-input ${errors.email ? "has-error" : ""}`}
                      />
                      {errors.email && (
                        <span className="launch-field-error">{errors.email}</span>
                      )}
                    </div>

                    <div className="launch-form-group half-width">
                      <label htmlFor="launch-company" className="launch-label">
                        COMPANY NAME
                      </label>
                      <input
                        id="launch-company"
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        autoComplete="organization"
                        required
                        className={`launch-input ${errors.company ? "has-error" : ""}`}
                      />
                      {errors.company && (
                        <span className="launch-field-error">{errors.company}</span>
                      )}
                    </div>
                  </div>

                  {/* SUBMIT BUTTON ROW */}
                  <div className="launch-submit-row">
                    {status === "success" && (
                      <div className="launch-status-msg success">
                        {statusMessage}
                      </div>
                    )}
                    {status === "error" && (
                      <div className="launch-status-msg error">
                        {statusMessage}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="launch-submit-btn"
                      disabled={status === "submitting"}
                      aria-label="Submit inquiry"
                    >
                      {status === "submitting" ? (
                        <span className="launch-submitting-text">SENDING...</span>
                      ) : (
                        <span className="launch-submit-text">SUBMIT</span>
                      )}
                    </button>
                  </div>
                </form>

                {/* BOTTOM CONTACT INFO */}
                <div className="launch-bottom-info">
                  <div className="launch-info-row">
                    <a
                      href="mailto:launch@2lightyears.com"
                      className="launch-info-email"
                    >
                      <svg
                        className="launch-info-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <rect width="20" height="16" x="2" y="4" rx="2" />
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                      </svg>
                      <span>launch@2lightyears.com</span>
                    </a>
                  </div>

                  <div className="launch-info-address" style={{ textTransform: 'capitalize' }}>
                    <svg
                      className="launch-info-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <span>Nirman Nagar, Jaipur, Rajasthan 302019</span>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </PageTransition>

      <Footer visible mode="normal" />
    </>
  );
}
